import axiosInstance from "@/api/axios";
import { LlmType } from "@/constants";
import { configToUse } from "@/Keycloak";
import { Account, EAdAccountPlatform, IAccount, Logo, Metric, Paginator } from "@/types";
import { LibraryMedia } from "@/types";
import { CancelToken } from "axios";

interface UserProfileResponse {
  name: string;
  email: string;
  company: string;
  role: string;
  profile_picture: string;
  support_hash?: string;
  llm_type: LlmType;
  llm_model: string;
  free_trial_expired: boolean;
  permission: {
    [key: string]: {
      [key: string]: boolean;
    };
  };
}
export const getUserProfile = async () => {
  const res = await axiosInstance.get<UserProfileResponse>(`/accounts/get-user-info/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const fetchAccounts = async ({
  platform = "ALL",
  page = 1,
  search = "",
  pageSize = 20,
  signal,
}: {
  platform: string;
  page?: number;
  search?: string;
  pageSize?: number;
  signal?: AbortSignal;
}) => {
  const res = await axiosInstance.get<{
    results: Account[];
    pagination: Paginator;
  }>(`/insights-v2/accounts/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    params: {
      platform: platform,
      page: page,
      search: search,
      page_size: pageSize,
    },
    signal,
  });
  return res.data;
};

export const fetchAccountById = async (id: string) => {
  const res = await axiosInstance.get<Account>(`/insights-v2/account/${id}/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const getLibraryMedia = async (accountId: string) => {
  const res = await axiosInstance.get<{ data: LibraryMedia[]; status: string }>(
    `/creative-collection/${accountId}/creative-library/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteLibraryMedia = async (id: string, accountId: string) => {
  const res = await axiosInstance.delete<{ status: string; message: string }>(
    `/creative-collection/${accountId}/creative-library/${id}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const addLibraryMedia = async (file_ids: string[], accountId: string) => {
  const res = await axiosInstance.post<{ status: string; data: LibraryMedia[] }>(
    `/creative-collection/${accountId}/creative-library/`,
    {
      file_ids,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const uploadFiles = async (
  files: File[],
  accountId: string,
  usageType?: string,
) => {
  // Create a FormData object to handle the files
  const formData = new FormData();

  // Append each file to the FormData object
  files.forEach((file) => {
    formData.append("files", file); // 'files' is the key used in the body
  });

  // Also append account_id and usage_type fields (if present)
  if (accountId) {
    formData.append("account_id", accountId);
  }
  if (usageType) {
    formData.append("usage_type", usageType);
  }

  // Send the request using axiosInstance
  const res = await axiosInstance.post<{
    files: {
      id: string;
      file_name: string;
      public_url: string;
    }[];
    message: string;
  }>(`/chat/upload/`, formData, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const uploadFilesAsync = async (
  files: File[],
  accountId: string,
  usageType?: string,
): Promise<{
  files: Array<{ id: string; file_name: string; public_url: string }>;
  message: string;
}> => {
  // Prepare file metadata for the request
  const filesMetadata = files.map((file) => ({
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
  }));

  // Request presigned URLs
  const res = await axiosInstance.post<{
    message: string;
    files: {
      id: string;
      file_name: string;
      public_url: string;
      presigned_upload_url: string;
    }[];
  }>(
    `/chat/upload/`,
    {
      files: filesMetadata,
      account_id: accountId,
      usage_type: usageType,
      use_async: true,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );

  // Upload each file to its presigned URL
  const uploadPromises = res.data.files.map(async (fileInfo, index) => {
    const file = files[index];
    const presignedUrl = fileInfo.presigned_upload_url;

    // Extract content-type from presigned URL or use file type
    const contentType = file.type || "application/octet-stream";

    // Upload file to presigned URL
    const response = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": contentType,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to upload ${file.name}: ${response.statusText}`);
    }

    return {
      id: fileInfo.id,
      file_name: fileInfo.file_name,
      public_url: fileInfo.public_url,
    };
  });

  // Wait for all uploads to complete
  const uploadedFiles = await Promise.all(uploadPromises);

  return {
    files: uploadedFiles,
    message: res.data.message,
  };
};

export const connectAccounts = async (accountIds: string[]) => {
  const res = await axiosInstance.post<{
    message: string;
    account_ids: string[];
  }>(
    `/accounts/connect-accounts/`,
    { account_ids: accountIds },
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res;
};

export const startGoogleAdsAuth = async (redirectUrl: string, platform: string, accountId?: string) => {
  const res = await axiosInstance.get<string>(`/accounts/oauth-sign-in/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    params: {
      redirect_url: redirectUrl,
      platform: platform,
      account_id: accountId,
    },
  });
  return res.data;
};

export const fetchPendingAccounts = async (platform: string) => {
  const res = await axiosInstance.get<IAccount[]>(`/accounts/fetch-pending-accounts/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    params: {
      platform: platform,
    },
  });
  return res.data;
};

export const updateAccountLogos = async (accountId: string, file_ids: string[]) => {
  const res = await axiosInstance.put<{
    logos: Logo[];
  }>(
    `/accounts/${accountId}/update-account-logos/`,
    {
      file_ids,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchMetrics = async (
  accountId?: string,
  assetGroupConfigId?: string,
  metricType?: "EXTRA" | "ALL",
) => {
  const res = await axiosInstance.get<Metric[]>(`/insights-v2/metrics/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    params: {
      account_id: accountId,
      asset_group_configuration_id: assetGroupConfigId,
      metric_type: metricType,
    },
  });
  return res.data;
};

export const setDefaultAccount = async (accountId: string | null) => {
  const res = await axiosInstance.post<{
    message: string;
    account?: Account;
  }>(
    `/insights-v2/accounts/set-default/`,
    { account_id: accountId },
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

export const mergeAccount = async (data: {
  account_ids: string[];
  account_name: string;
}) => {
  const res = await axiosInstance.post<Account>(`/accounts/merge-accounts/`, data, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export interface OAuthConnectionJob {
  job_id: string;
  platform: EAdAccountPlatform | string;
  status: string;
  created_at?: string;
}

export const fetchActiveOAuthConnections = async (): Promise<OAuthConnectionJob[]> => {
  const res = await axiosInstance.get<OAuthConnectionJob[]>(`/accounts/oauth-connections/active/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const fetchAssets = async (
  accountId: string,
  assetType: string,
  searchTerm: string = "",
  options: {
    signal?: AbortSignal;
  } = {},
): Promise<string[]> => {
  const res = await axiosInstance.get<string[]>(`/creative/${accountId}/search-assets/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    params: {
      asset_type: assetType,
      search_term: searchTerm,
    },
    signal: options.signal,
  });
  return res.data as string[];
};

export const getUniqueLabels = async (
  accountId: string,
  labelId: string,
  sectionId?: string,
  cancelToken?: CancelToken,
): Promise<{ status: string; unique_labels: string[] }> => {
  const res = await axiosInstance.get<{ status: string; unique_labels: string[] }>(
    `/creative/${accountId}/label/${labelId}/unique-labels/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        ...(sectionId && { section_id: sectionId }),
      },
      cancelToken,
    },
  );
  return res.data as { status: string; unique_labels: string[] };
};
